import { z } from 'zod';
/**
 * The wire contract. Shared by the API (validation) and the web/mobile clients
 * (form typing), so a field cannot drift between them.
 *
 * Convention: everything is METRIC on the wire (cm, kg, m3). Imperial exists
 * only at the display boundary — see units.ts.
 */
export const uuid = z.string().uuid();
export const iso4217 = z.string().length(3).regex(/^[A-Z]{3}$/, 'currency must be an ISO-4217 code');
export const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
export const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/, 'expected HH:MM');
export const paginationSchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
// ---------------------------------------------------------------- enums
export const userRole = z.enum(['transporter', 'client', 'admin']);
export const unitSystem = z.enum(['metric', 'imperial']);
// Freight body styles first, then passenger. bus_cargo is the odd one out and
// stays freight: it is a bus shell used to move goods, not people.
export const FREIGHT_VEHICLE_TYPES = ['van', 'pickup', 'truck', 'container', 'bus_cargo', 'other'];
export const PASSENGER_VEHICLE_TYPES = ['bus', 'coach', 'minibus', 'car', 'suv'];
export const vehicleType = z.enum([...FREIGHT_VEHICLE_TYPES, ...PASSENGER_VEHICLE_TYPES]);
export const vehicleStatus = z.enum(['active', 'maintenance', 'retired']);
export const recurrenceType = z.enum(['one_off', 'weekly', 'monthly', 'yearly']);
export const deliveryMode = z.enum(['delivered_to_address', 'collect_from_hub']);
export const paymentMethod = z.enum(['cash', 'bank_transfer', 'cheque', 'card', 'other']);
export const devicePlatform = z.enum(['android', 'ios', 'web']);
export const deviceApp = z.enum(['transporter', 'client']);
// ---------------------------------------------------------------- me
export const onboardingSchema = z.object({
    role: userRole.exclude(['admin']),
    country_id: uuid,
    language_code: z.string().min(2).max(5),
    unit_system: unitSystem.optional(),
    full_name: z.string().min(1).max(120).optional(),
    phone: z.string().max(40).optional(),
    // transporter only
    company_name: z.string().min(1).max(160).optional(),
    hub_address: z.string().max(400).optional(),
    hub_city_id: uuid.optional(),
}).superRefine((v, ctx) => {
    if (v.role === 'transporter' && !v.company_name) {
        ctx.addIssue({ code: 'custom', path: ['company_name'], message: 'company_name is required for a transporter' });
    }
});
export const updateProfileSchema = z.object({
    full_name: z.string().min(1).max(120).optional(),
    phone: z.string().max(40).optional(),
    country_id: uuid.optional(),
    language_code: z.string().min(2).max(5).optional(),
    unit_system: unitSystem.optional(),
    timezone: z.string().max(64).optional(),
});
export const deviceTokenSchema = z.object({
    fcm_token: z.string().min(10),
    platform: devicePlatform,
    app: deviceApp,
});
// ---------------------------------------------------------------- fleet
const dimension = z.number().positive().max(2000);
// Declared here rather than in the passenger section below because
// vehicleCreateSchema needs them, and a const is not hoisted.
export const vehicleMode = z.enum(['freight', 'passenger']);
/**
 * What the vehicle is like inside, as a closed vocabulary.
 *
 * Mirrors vehicles_amenities_known in 0063. Free text cannot be translated
 * into seventeen languages, drawn as an icon, or filtered on, and would
 * arrive as "wifi", "WiFi" and "wi-fi" from three different carriers.
 * Adding one is a migration AND a line here AND a translated label -- which
 * is the right amount of friction for a claim that appears on a licensed
 * carrier's public page.
 */
/*
 * Seventeen since 0064, nine before it.
 *
 * The eight added are the questions a pilgrim asks that no field could hold:
 * a washroom on board, a Zamzam cooler, a prayer stop on a long run, a
 * separate section for women, a 220V socket (which is not the claim USB
 * charging makes), a meal, a screen, and an underfloor hold rather than
 * "the bags fit on your lap". The order below is the order a traveller
 * cares about them, and every screen renders in this order.
 */
export const VEHICLE_AMENITIES = [
    'ac', 'wifi', 'usb', 'socket', 'tracking', 'water', 'zamzam',
    'recliner', 'curtains', 'washroom', 'meal', 'screen', 'luggage_hold',
    'prayer_stop', 'ladies_section', 'wheelchair', 'child_seat',
];
export const vehicleAmenity = z.enum(VEHICLE_AMENITIES);
export const seatTemplate = z.enum(['2+2', '2+1', '3+2', '2+0', '1+1']);
export const seatLayoutSchema = z.object({
    template: seatTemplate,
    rows: z.number().int().min(1).max(30),
    business_rows: z.number().int().min(0).max(30).default(0),
    blocked: z.array(z.string().max(4)).max(120).default([]),
});
/*
 * ---------------------------------------------------------------- carriers
 *
 * The application form from the mockup's "Apply to carry" sheet, field for
 * field, and the company edit behind the first pane of the setup hub.
 *
 * Why the four paper numbers are columns and not a notes field: they are what
 * the vetting IS. The owner reads the CR to know the company exists, the TGA
 * licence to know it may legally carry passengers (§46.2 -- the real gate on
 * this product), the VAT number because an invoice that charges VAT must show
 * it (§61), and the national address because that is the Saudi addressing
 * format an inspector and a courier both use.
 *
 * Only the trading name is required. An applicant standing in a car park
 * should be able to get the application in and be contacted; an incomplete one
 * is the owner's judgement to make, not a form's.
 */
export const carrierApplicationSchema = z.object({
    company_name: z.string().min(1).max(160),
    legal_name: z.string().max(160).nullish(),
    cr_no: z.string().max(40).nullish(),
    transport_licence_no: z.string().max(60).nullish(),
    vat_no: z.string().max(40).nullish(),
    national_address: z.string().max(300).nullish(),
    hub_address: z.string().max(300).nullish(),
    hub_city_id: uuid.nullish(),
    contact_name: z.string().max(120).nullish(),
    phone: z.string().max(40).nullish(),
    whatsapp: z.string().max(40).nullish(),
    note: z.string().max(1000).nullish(),
});
/**
 * Editing the company. Deliberately NOT the application schema partialled:
 * status, applied_at and verified_at are Qafila's to set, and a carrier who
 * could patch his own `status` would be approving himself.
 */
export const companyUpdateSchema = z.object({
    company_name: z.string().min(1).max(160).optional(),
    legal_name: z.string().max(160).nullish(),
    phone: z.string().max(40).nullish(),
    whatsapp: z.string().max(40).nullish(),
    hub_address: z.string().max(300).nullish(),
    hub_city_id: uuid.nullish(),
    cr_no: z.string().max(40).nullish(),
    transport_licence_no: z.string().max(60).nullish(),
    vat_no: z.string().max(40).nullish(),
    national_address: z.string().max(300).nullish(),
});
/**
 * HIS REFUND AND CHANGE TERMS (M1, 0064).
 *
 * Its own schema and its own endpoint, deliberately apart from
 * companyUpdateSchema. The company record is locked after approval because
 * the CR, the licence and the VAT number are what an admin READ before
 * letting him sell -- but a refund policy is not a fact anybody approved.
 * It is a commercial undertaking he gives his own customers, it changes with
 * the season, and he must be able to change it without asking us.
 *
 * Every field nullable: clearing a term is a real answer, and a carrier who
 * has stated nothing is shown to the traveller as having stated nothing.
 * There is no default anywhere in this chain -- a default here would be us
 * writing a refund policy on a licensed carrier's behalf.
 */
export const carrierTermsSchema = z.object({
    cancel_free_hours: z.coerce.number().int().min(0).max(720).nullish(),
    cancel_fee_pct: z.coerce.number().min(0).max(100).nullish(),
    change_free_hours: z.coerce.number().int().min(0).max(720).nullish(),
    change_fee_pct: z.coerce.number().min(0).max(100).nullish(),
    /* His own words, printed verbatim and never translated -- so capped at a
       length that stays readable on a phone rather than becoming a document
       nobody reads. Mirrors transporters_terms_note_len in 0064. */
    terms_note: z.string().max(600).nullish(),
});
export const vehicleCreateSchema = z.object({
    name: z.string().min(1).max(120),
    type: vehicleType,
    plate_no: z.string().max(40).nullish(),
    photos: z.array(z.string().url()).default([]),
    stock_illustration: z.string().max(80).nullish(),
    // A coach has no cargo box and a lorry has no seats, so both halves are
    // optional here and the refinement below insists on whichever the mode needs.
    box_l_cm: dimension.optional(),
    box_w_cm: dimension.optional(),
    box_h_cm: dimension.optional(),
    max_weight_kg: z.number().positive().max(100_000).optional(),
    mode: vehicleMode.default('freight'),
    seat_layout: seatLayoutSchema.optional(),
    /*
     * The comfort facts, all optional: a fleet entered before 0063 is not
     * suddenly invalid, it is simply not described yet, and every screen
     * prints only what was actually entered.
     */
    doors: z.number().int().min(1).max(8).nullish(),
    bags: z.number().int().min(0).max(200).nullish(),
    amenities: z.array(vehicleAmenity).max(VEHICLE_AMENITIES.length).default([]),
    notes: z.string().max(2000).nullish(),
    status: vehicleStatus.default('active'),
}).superRefine((v, ctx) => {
    // Mirrors the vehicles_has_image check constraint: a listing is never image-less,
    // because the client judges the vehicle by its picture.
    if (v.photos.length === 0 && !v.stock_illustration) {
        ctx.addIssue({
            code: 'custom', path: ['photos'],
            message: 'provide at least one photo, or a stock_illustration',
        });
    }
    // Mirrors vehicles_mode_shape in 0013. Rejecting here gives a field-level
    // message instead of a constraint violation the client cannot act on.
    if (v.mode === 'freight') {
        for (const f of ['box_l_cm', 'box_w_cm', 'box_h_cm', 'max_weight_kg']) {
            if (v[f] === undefined) {
                ctx.addIssue({ code: 'custom', path: [f], message: 'required for a freight vehicle' });
            }
        }
    }
    else {
        if (!v.seat_layout) {
            ctx.addIssue({
                code: 'custom', path: ['seat_layout'],
                message: 'a passenger vehicle needs a seat layout',
            });
        }
    }
});
export const vehicleUpdateSchema = vehicleCreateSchema.innerType().partial();
export const routeCreateSchema = z.object({
    from_city_id: uuid,
    to_city_id: uuid,
    /*
     * The NAMED endpoints, and the reason 0036 added the columns.
     *
     * A Makkah leg is not "Jeddah to Makkah" -- it is the airport to the
     * Haram, and a pilgrim picks the place, not the city. Every one of the six
     * seeded legs carries these, but they were inserted in SQL because this
     * schema never accepted them, so a route built in the app came out
     * city-level and read differently from every route beside it (§63).
     *
     * Nullish, not required: "anywhere in Jeddah" is a real answer, and it is
     * the only answer available for a city with no points yet.
     */
    from_point_id: uuid.nullish(),
    to_point_id: uuid.nullish(),
    distance_km: z.number().positive().max(30_000).optional(),
    duration_min: z.number().int().positive().max(20_000).optional(),
    stops: z.array(uuid).max(10).default([]),
}).superRefine((v, ctx) => {
    /*
     * Mirrors routes_distinct_endpoints, which is laxer than the old refine:
     * the SAME city is allowed when the two points differ, because Al Haram to
     * Kudai terminal is a real journey someone sells. Rejecting it here while
     * the database permits it was costing the app a route it is allowed to
     * have.
     */
    const samePlace = v.from_city_id === v.to_city_id
        && !(v.from_point_id && v.to_point_id && v.from_point_id !== v.to_point_id);
    if (samePlace) {
        ctx.addIssue({
            code: 'custom', path: ['to_city_id'],
            message: 'origin and destination must differ',
        });
    }
});
export const scheduleCreateSchema = z.object({
    route_id: uuid,
    vehicle_id: uuid,
    // Overrides the vehicle's current driver for trips this schedule creates.
    // Null (the default) follows the vehicle -- see 0017_schedule_driver_override.sql.
    driver_id: uuid.nullish(),
    // Passenger fares: per seat, per class, before tax. The country's VAT/GST is
    // added at sale time and frozen onto the booking.
    fare_business: z.number().min(0).max(10_000_000).nullish(),
    fare_regular: z.number().min(0).max(10_000_000).nullish(),
    fare_currency: iso4217.nullish(),
    // Told to the passenger BEFORE they book, not when they try to cancel.
    seats_cancellable: z.boolean().default(true),
    /** The transporter must say yes before a seat request becomes a booking. */
    confirmation_required: z.boolean().default(false),
    /** Share of the ALREADY PUBLISHED fare that falls due on confirmation. */
    deposit_pct: z.number().min(0).max(100).default(0),
    recurrence: recurrenceType,
    weekdays: z.array(z.number().int().min(1).max(7)).max(7).optional(),
    day_of_month: z.number().int().min(1).max(31).optional(),
    valid_from: isoDate,
    valid_to: isoDate.nullish(),
    depart_time: hhmm,
    duration_min: z.number().int().positive().max(20_000),
    timezone: z.string().min(3).max(64).optional(),
}).superRefine((v, ctx) => {
    if (v.recurrence === 'weekly' && !v.weekdays?.length) {
        ctx.addIssue({ code: 'custom', path: ['weekdays'], message: 'weekly schedules need at least one weekday' });
    }
    if (v.recurrence === 'monthly' && !v.day_of_month) {
        ctx.addIssue({ code: 'custom', path: ['day_of_month'], message: 'monthly schedules need a day_of_month' });
    }
    if (v.valid_to && v.valid_to < v.valid_from) {
        ctx.addIssue({ code: 'custom', path: ['valid_to'], message: 'valid_to is before valid_from' });
    }
});
export const tripCreateSchema = z.object({
    route_id: uuid,
    vehicle_id: uuid,
    // Passenger fares: per seat, per class, before tax. The country's VAT/GST is
    // added at sale time and frozen onto the booking.
    fare_business: z.number().min(0).max(10_000_000).nullish(),
    fare_regular: z.number().min(0).max(10_000_000).nullish(),
    fare_currency: iso4217.nullish(),
    // Told to the passenger BEFORE they book, not when they try to cancel.
    seats_cancellable: z.boolean().default(true),
    /** The transporter must say yes before a seat request becomes a booking. */
    confirmation_required: z.boolean().default(false),
    /** Share of the ALREADY PUBLISHED fare that falls due on confirmation. */
    deposit_pct: z.number().min(0).max(100).default(0),
    trip_date: isoDate,
    depart_time: hhmm,
    duration_min: z.number().int().positive().max(20_000),
    timezone: z.string().min(3).max(64).optional(),
});
export const cancelSchema = z.object({
    remark: z.string().max(500).optional(),
});
// ---------------------------------------------------------------- demand
export const requestItemSchema = z.object({
    item_type_id: uuid,
    description: z.string().max(400).optional(),
    qty: z.number().int().positive().max(999).default(1),
    l_cm: dimension,
    w_cm: dimension,
    h_cm: dimension,
    weight_kg: z.number().positive().max(50_000),
    photo_url: z.string().url().optional(),
});
/**
 * The published terms, by date.
 *
 * Bumped whenever the wording changes in a way that alters what someone is
 * agreeing to. Stored on every request and booking, so a dispute can be
 * answered with the exact words that were on screen rather than today's.
 */
export const TERMS_VERSION = '2026-08-30';
/**
 * The two ticks. Literally `true`, not merely present: `z.boolean()` would
 * accept `false` and record a consent nobody gave.
 */
const consentSchema = {
    /** "The sizes and weights I have entered are correct." */
    confirm_details: z.literal(true),
    /** "I accept the terms." */
    accept_terms: z.literal(true),
};
export const requestCreateSchema = z.object({
    ...consentSchema,
    from_city_id: uuid,
    to_city_id: uuid,
    preferred_date: isoDate.nullish(),
    flexible_days: z.number().int().min(0).max(14).default(3),
    trip_id: uuid.nullish(),
    vehicle_id: uuid.nullish(),
    items: z.array(requestItemSchema).min(1).max(50),
    notes: z.string().max(2000).optional(),
    target_transporter_ids: z.array(uuid).max(25).optional(),
}).refine((v) => v.from_city_id !== v.to_city_id, {
    path: ['to_city_id'], message: 'origin and destination must differ',
});
/**
 * A charter: one vehicle, one group, every transporter asked.
 *
 * Deliberately NOT a variant of requestCreateSchema. A freight request is
 * described by its items and a charter by its headcount, and bolting an
 * optional `items` onto one schema would let a caller send a charter with
 * three cartons in it and leave the server to decide what that means.
 *
 * Points, not just cities. "Jeddah to Makkah" is not an instruction anybody
 * can act on at 02:40; "King Abdulaziz International Airport to Al Haram"
 * is. They are optional because a charter between two city centres is still
 * a charter.
 */
export const charterCreateSchema = z.object({
    /*
     * Terms only -- no "the sizes I entered are correct".
     *
     * The same reasoning passengerBookSchema already uses for a seat: a
     * charter declares no dimensions and no weights, so a tick confirming
     * them is a tick with nothing behind it. The review sheet shown before
     * this call IS the confirmation of the details, and the server stamps
     * details_confirmed_at when it succeeds.
     */
    accept_terms: z.literal(true),
    from_city_id: uuid,
    to_city_id: uuid,
    from_point_id: uuid.nullish(),
    to_point_id: uuid.nullish(),
    preferred_date: isoDate.nullish(),
    flexible_days: z.number().int().min(0).max(14).default(1),
    /** Headcount, not seats sold. 60 is a large coach and a sane ceiling. */
    passengers: z.number().int().min(1).max(60),
    /*
     * The class the client wants to turn up -- the mockup's SUV / Hiace /
     * Coaster picker. A wish, not an assignment: the carrier still quotes with
     * one of their own vehicles and may offer another class at another price.
     */
    preferred_vehicle_type: vehicleType.nullish(),
    /*
     * Legs 2 upwards. Leg 1 is the from/to above, which is what every
     * existing query reads, so a one-leg charter sends none of these.
     * Four is the practical ceiling for a pilgrim itinerary -- airport,
     * Makkah, Madinah, airport -- and a sane bound on the fan-out.
     */
    extra_legs: z.array(z.object({
        from_city_id: uuid,
        to_city_id: uuid,
        from_point_id: uuid.nullish(),
        to_point_id: uuid.nullish(),
        leg_date: isoDate.nullish(),
    })).max(4).optional(),
    notes: z.string().max(2000).optional(),
}).superRefine((v, ctx) => {
    // Mirrors requests_distinct_endpoints (0045), so the rejection is a field
    // message rather than a constraint violation the client cannot act on.
    const sameCity = v.from_city_id === v.to_city_id;
    const bothPoints = Boolean(v.from_point_id && v.to_point_id);
    if (sameCity && !(bothPoints && v.from_point_id !== v.to_point_id)) {
        ctx.addIssue({
            code: 'custom', path: ['to_city_id'],
            message: 'origin and destination must differ, or be two different points in one city',
        });
    }
});
export const fitCheckSchema = z.object({
    trip_id: uuid,
    items: z.array(requestItemSchema).min(1).max(50),
});
export const quotationCreateSchema = z.object({
    /*
     * NULL for a charter.
     *
     * A charter has no scheduled departure -- the carrier is offering to run
     * one -- so there is no trip to attach the price to. The vehicle stays
     * required either way: a price with no vehicle behind it tells the client
     * nothing about what is turning up. Which kind of request this is comes
     * from the REQUEST on the server, never from what the caller chose to
     * send, or a carrier could dodge the capacity check on an ordinary load
     * by simply omitting trip_id.
     */
    trip_id: uuid.nullish(),
    vehicle_id: uuid,
    amount: z.number().positive().max(100_000_000),
    currency: iso4217,
    remarks: z.string().max(1000).optional(),
    valid_until: z.string().datetime({ offset: true }),
});
/**
 * The client's counter. No trip or vehicle: a counter argues about the price
 * of the offer already on the table, never about which van it rides in. The
 * note is what the transporter reads before deciding, and it is kept in the
 * recorded thread either way.
 */
export const quotationCounterSchema = z.object({
    amount: z.number().positive().max(100_000_000),
    note: z.string().max(1000).optional(),
});
// ---------------------------------------------------------------- fulfilment
export const receiveOrderItemSchema = z.object({
    id: uuid,
    actual_l_cm: dimension.optional(),
    actual_w_cm: dimension.optional(),
    actual_h_cm: dimension.optional(),
    actual_weight_kg: z.number().positive().max(50_000),
});
export const receiveOrderSchema = z.object({
    delivery_mode: deliveryMode,
    destination_address: z.string().max(400).optional(),
    destination_contact_name: z.string().max(120).optional(),
    destination_contact_phone: z.string().max(40).optional(),
    hub_address: z.string().max(400).optional(),
    items: z.array(receiveOrderItemSchema).min(1),
    final_amount: z.number().positive().max(100_000_000).optional(),
    amount_change_reason: z.string().max(500).optional(),
}).superRefine((v, ctx) => {
    if (v.delivery_mode === 'delivered_to_address') {
        for (const f of ['destination_address', 'destination_contact_name', 'destination_contact_phone']) {
            if (!v[f]) {
                ctx.addIssue({ code: 'custom', path: [f], message: 'required when delivering to an address' });
            }
        }
    }
});
export const collectSchema = z.object({
    picker_name: z.string().min(1).max(120),
    picker_phone: z.string().min(3).max(40),
});
export const deliverSchema = z.object({
    receiver_name: z.string().min(1).max(120),
    receiver_phone: z.string().min(3).max(40),
});
export const rateSchema = z.object({
    stars: z.number().int().min(1).max(5),
    comment: z.string().max(1000).optional(),
});
export const markPaidSchema = z.object({
    method: paymentMethod.default('cash'),
    amount: z.number().positive().optional(), // defaults to the amount due
    reference: z.string().max(120).optional(),
    note: z.string().max(500).optional(),
});
export const refundSchema = z.object({
    amount: z.number().positive().optional(),
    reason: z.string().min(1).max(500),
});
// ---------------------------------------------------------------- discovery
export const discoverTripsQuery = z.object({
    from_city_id: uuid,
    to_city_id: uuid,
    date: isoDate.optional(),
    days: z.coerce.number().int().min(1).max(14).default(3),
});
export const nearestCityQuery = z.object({
    lat: z.coerce.number().min(-90).max(90),
    lng: z.coerce.number().min(-180).max(180),
    max_km: z.coerce.number().positive().max(1000).default(50),
});
// ---------------------------------------------------------------- chat
export const chatMessageSchema = z.object({
    content: z.string().min(1).max(4000),
});
// ---------------------------------------------------------------- passenger
//
// Seat sales are instant at the published fare, so they never create a request
// or a quotation. Chartering a whole vehicle still does, because that is a
// negotiation -- see requestCreateSchema's whole_vehicle flag.
export const seatClass = z.enum(['business', 'regular']);
/** Stop-to-stop leg. Origin is 0, each route stop its own seq, destination last. */
const legSchema = z.object({
    from_seq: z.number().int().min(0).max(20),
    to_seq: z.number().int().min(1).max(21),
}).refine((v) => v.to_seq > v.from_seq, {
    path: ['to_seq'], message: 'to_seq must be after from_seq',
});
export const seatSearchSchema = z.object({
    from_city_id: uuid,
    to_city_id: uuid,
    date: isoDate.nullish(),
    // Coerced, not plain numbers: this schema parses a QUERY STRING, where every
    // value arrives as text. z.number() here made the whole seat search a 400.
    flexible_days: z.coerce.number().int().min(0).max(14).default(3),
    seats: z.coerce.number().int().min(1).max(60).default(1),
    seat_class: seatClass.nullish(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
}).refine((v) => v.from_city_id !== v.to_city_id, {
    path: ['to_city_id'], message: 'origin and destination must differ',
});
export const passengerBookSchema = z.object({
    /*
     * Terms only.
     *
     * A seat has no declared dimensions, so asking someone to confirm sizes they
     * never entered is a tick with nothing behind it. The review sheet shown
     * before this call IS the confirmation of the details, and the server stamps
     * `details_confirmed_at` when it succeeds.
     */
    accept_terms: z.literal(true),
    /*
     * THE CARRIER'S OWN REFUND AND CHANGE TERMS (M1, 0064).
     *
     * Separate from accept_terms, which is ours. This one is his: what
     * happens to this traveller's money if they cannot travel, in the
     * figures he published. Optional here and REQUIRED by the route when he
     * has actually stated terms -- a carrier who has published none has
     * nothing to consent to, and a tick with nothing behind it is the thing
     * this whole change exists to remove.
     */
    accept_carrier_terms: z.literal(true).optional(),
    trip_id: uuid,
    from_seq: z.number().int().min(0).max(20),
    to_seq: z.number().int().min(1).max(21),
    /** Specific seats, chosen from the map. Airlines let you pick; so do we. */
    seat_labels: z.array(z.string().min(1).max(4)).min(1).max(60),
    contact_name: z.string().max(120).optional(),
    contact_phone: z.string().max(40).optional(),
}).refine((v) => v.to_seq > v.from_seq, {
    path: ['to_seq'], message: 'to_seq must be after from_seq',
});
// ---------------------------------------------------------------- drivers
export const driverCreateSchema = z.object({
    login_id: z.string().regex(/^[a-z0-9][a-z0-9._-]{2,39}$/, 'lowercase letters, digits, dot, dash or underscore; 3-40 characters'),
    full_name: z.string().min(1).max(120),
    password: z.string().min(8).max(72),
    phone: z.string().max(40).optional(),
    licence_no: z.string().max(60).optional(),
});
export const driverUpdateSchema = z.object({
    /**
     * The sign-in id, same rules as creation. Editable because a transporter who
     * mistyped it when adding the driver otherwise had no way back -- the driver
     * simply could not sign in, and nothing on the page explained why.
     */
    login_id: z.string().regex(/^[a-z0-9][a-z0-9._-]{2,39}$/, 'lowercase letters, digits, dot, dash or underscore; 3-40 characters').optional(),
    full_name: z.string().min(1).max(120).optional(),
    phone: z.string().max(40).nullish(),
    licence_no: z.string().max(60).nullish(),
    status: z.enum(['active', 'suspended']).optional(),
    /** Setting a new password never reads the old one; the transporter owns it. */
    password: z.string().min(8).max(72).optional(),
});
export const driverSignInSchema = z.object({
    login_id: z.string().min(3).max(40),
    password: z.string().min(1).max(72),
});
/** A position sample from the driver's phone. Batched, because signal drops. */
export const locationPingSchema = z.object({
    points: z.array(z.object({
        lat: z.number().min(-90).max(90),
        lng: z.number().min(-180).max(180),
        speed_kph: z.number().min(0).max(400).nullish(),
        heading: z.number().min(0).max(360).nullish(),
        recorded_at: z.string().datetime({ offset: true }),
    })).min(1).max(200),
    trip_id: uuid.nullish(),
});
/** The driver selling a seat at the roadside, for cash, offline. */
export const roadsideSaleSchema = z.object({
    trip_id: uuid,
    from_seq: z.number().int().min(0).max(20),
    to_seq: z.number().int().min(1).max(21),
    seat_labels: z.array(z.string().min(1).max(4)).min(1).max(60),
    passenger_name: z.string().max(120).optional(),
    passenger_phone: z.string().max(40).optional(),
    /** Cash actually taken. Defaults to the trip fare when omitted. */
    amount: z.number().min(0).max(10_000_000).optional(),
    /** The driver's own id for the sale, so a queued offline sale is idempotent. */
    client_ref: z.string().max(64).optional(),
}).refine((v) => v.to_seq > v.from_seq, {
    path: ['to_seq'], message: 'to_seq must be after from_seq',
});
export const scanTicketSchema = z.object({
    trip_id: uuid,
    /** Raw QR contents; the server re-parses rather than trusting the app. */
    code: z.string().min(4).max(200),
    scanned_at: z.string().datetime({ offset: true }).optional(),
});
export const settlementSchema = z.object({
    amount: z.number().positive().max(100_000_000),
    currency: iso4217,
    method: z.enum(['cash', 'bank_transfer', 'cheque', 'card', 'other']).default('cash'),
    reference: z.string().max(120).optional(),
    note: z.string().max(500).optional(),
});
/* ──────────────────────────────────────────────────────────────────────────
   THE CARRIER'S OWN SITE  (§81)

   A request composed on a carrier's own site, and the written quotation he
   answers it with. Both live here rather than in the API because
   apps/carrier-site validates the same shapes before it posts them.
   ────────────────────────────────────────────────────────────────────────── */
/** One leg of a journey, as the traveller describes it on his form. */
export const siteLegSchema = z.object({
    from_city_id: uuid,
    to_city_id: uuid,
    from_point_id: uuid.nullish(),
    to_point_id: uuid.nullish(),
    /** Local calendar date in Saudi Arabia. The site shows every time as KSA. */
    on: z.string().date(),
    at: z.string().regex(/^\d{2}:\d{2}$/, 'Use HH:MM, Saudi time.').nullish(),
    note: z.string().trim().max(400).nullish(),
});
/**
 * No account, on purpose.
 *
 * The traveller arrives from the directory and types into the carrier's form.
 * Asking them to register first would lose most of them, and an account on
 * OUR system is the wrong thing to ask for: the relationship being formed is
 * theirs with the carrier.
 */
export const siteRequestSchema = z.object({
    name: z.string().trim().min(2).max(120),
    email: z.string().trim().email().max(200),
    phone: z.string().trim().min(6).max(40),
    country: z.string().trim().max(80).nullish(),
    /** Carried over from the directory link, so he answers in their language. */
    locale: z.string().trim().max(12).nullish(),
    passengers: z.number().int().min(1).max(60),
    children: z.number().int().min(0).max(40).default(0),
    preferred_vehicle_type: z.string().trim().max(40).nullish(),
    notes: z.string().trim().max(2000).nullish(),
    legs: z.array(siteLegSchema).min(1).max(8),
    /** The carrier is the controller of what was typed; he names himself. */
    consent: z.literal(true),
});
/**
 * A line on a written quotation.
 *
 * `amount` omitted or null means "included": it adds nothing to the subtotal
 * and prints as the word. A zero would also add nothing, but it prints as
 * "0.00", and 0.00 against a child seat reads as a mistake where "included"
 * reads as generosity.
 */
export const quotationLineSchema = z.object({
    label: z.string().trim().min(2).max(160),
    note: z.string().trim().max(300).nullish(),
    at: z.string().datetime({ offset: true }).nullish(),
    amount: z.number().min(0).max(1_000_000).nullish(),
});
/**
 * The carrier's answer. He writes the price: nothing suggests it, nothing
 * ranks it against another carrier's, and no algorithm of ours touches it.
 */
export const siteQuotationSchema = z.object({
    /**
     * Required, and checked against his own fleet.
     *
     * A charter is a named vehicle with a known seat count, and the carrier
     * knows which one he is sending. Leaving it open would let a quotation
     * promise six seats the fleet cannot supply, and would mean the ticket had
     * nothing to print a plate from.
     */
    vehicle_id: uuid,
    /** What the client reads: "GMC Suburban - 6 seats, chartered whole". */
    vehicle_label: z.string().trim().min(2).max(120),
    lines: z.array(quotationLineSchema).min(1).max(30),
    /** Stored per quotation: the rate that applied on the day is part of the document. */
    vat_rate: z.number().min(0).max(1).default(0.15),
    valid_hours: z.number().int().min(1).max(720).default(48),
    remarks: z.string().trim().max(2000).nullish(),
});
/** What the carrier may change about his own site. */
export const tenantUpdateSchema = z.object({
    mark: z.string().trim().min(2).max(40).optional(),
    accent: z.record(z.record(z.string().regex(/^\d{1,3} \d{1,3} \d{1,3}$/))).optional(),
    logo_url: z.string().trim().url().max(500).nullish(),
    theme_default: z.enum(['light', 'dark', 'white']).optional(),
    locales: z.array(z.string().trim().min(2).max(12)).min(1).max(24).optional(),
    office_hours: z.record(z.string().trim().max(40)).optional(),
    reply_within_mins: z.number().int().min(5).max(10080).optional(),
    deposit_pct: z.number().int().min(0).max(100).optional(),
    psp: z.enum(['moyasar', 'tap']).nullish(),
    psp_merchant_ref: z.string().trim().max(120).nullish(),
    mail_from: z.string().trim().email().max(200).nullish(),
    qafila_links: z.boolean().optional(),
}).strict();
/**
 * A carrier's own payment-provider credentials.
 *
 * He pastes them in from his provider's dashboard. They are HIS account:
 * the fare settles to him, and refunds and chargebacks are his. We hold them
 * only so his own website can drive them.
 *
 * `mode` defaults to test and has to be moved deliberately. A live key on a
 * site nobody has walked through is how a real card gets charged by a form
 * that does not work yet.
 */
export const pspKeysSchema = z.object({
    provider: z.enum(['moyasar', 'tap']),
    mode: z.enum(['test', 'live']).default('test'),
    publishable_key: z.string().trim().max(200).nullish(),
    /** Omitted leaves whatever is stored; null clears it. */
    secret_key: z.string().trim().min(8).max(300).nullish(),
    webhook_secret: z.string().trim().min(8).max(300).nullish(),
}).strict();
//# sourceMappingURL=schemas.js.map